import type { Colourway, ColourwayId } from "@/features/catalog/catalog"

/**
 * Combo packs: `quantity` mounts of one product, each in a colour the buyer picks, at one price.
 * Shared by the console, the shop and checkout.
 */

export const MIN_MOUNTS = 2
export const MAX_MOUNTS = 6

/** The cart and the price list key a combo by this, never a variant SKU. */
export const comboKey = (slug: string) => `combo:${slug}`

/**
 * The combo's price split across its mounts, in paise so the parts add back exactly: the first
 * mounts take the extra paisa. Each part is one order line's unit price.
 */
export function splitPrice(price: string | number, mounts: number): string[] {
  const paise = Math.round(Number(price) * 100)
  const base = Math.floor(paise / mounts)
  const extra = paise - base * mounts
  return Array.from({ length: mounts }, (_, i) => ((base + (i < extra ? 1 : 0)) / 100).toFixed(2))
}

/** What the mounts cost one by one, at their MRP: the price a combo is shown against. */
export const comboMrp = (unitMrp: string | number, mounts: number) =>
  (Math.round(Number(unitMrp) * 100) * mounts) / 100

/** The combo as the shop shows it. */
export type StorefrontCombo = {
  slug: string
  name: string
  summary: string
  quantity: number
  price: string
  /** The mounts' MRP together. */
  compareAt: number
  extras: string[]
  /** The pack in one picture: the mount in its three finishes. */
  image: string
  product: {
    slug: string
    name: string
    colourways: Colourway[]
  }
}

/** The colours a buyer starts from: in stock ones first, each used once before any repeats. */
export function startingColours(colourways: Colourway[], mounts: number): ColourwayId[] {
  const ordered = [...colourways.filter((c) => c.inStock), ...colourways.filter((c) => !c.inStock)]
  return Array.from({ length: mounts }, (_, i) => ordered[i % ordered.length]!.id)
}

/** How many of each colour a pick asks for, to check against stock. */
export function coloursNeeded(colours: readonly ColourwayId[]): Map<ColourwayId, number> {
  const need = new Map<ColourwayId, number>()
  for (const c of colours) need.set(c, (need.get(c) ?? 0) + 1)
  return need
}

/** What a pick asks that stock cannot give, one line per colour; empty when it all can. */
export function comboShortfall(colourways: Colourway[], colours: readonly ColourwayId[]): string[] {
  return [...coloursNeeded(colours)].flatMap(([id, n]) => {
    const c = colourways.find((x) => x.id === id)!
    if (c.stock >= n) return []
    return [c.stock > 0 ? `Only ${c.stock} ${c.name} left` : `${c.name} is sold out`]
  })
}

/**
 * The combos the shop's filters leave: a search matches the combo or its mount, "in stock" wants a
 * colour to pick from, and a colour filter keeps them all (every combo comes in every colour).
 * Price sorts order them by their own price; otherwise they keep the console's order.
 */
export function comboResults(
  combos: StorefrontCombo[],
  query: { q: string; inStock: boolean; sort: string },
): StorefrontCombo[] {
  const words = query.q.toLowerCase().split(/\s+/).filter(Boolean)
  const kept = combos.filter((c) => {
    if (query.inStock && !c.product.colourways.some((x) => x.inStock)) return false
    const about = `${c.name} ${c.summary} ${c.product.name} combo pack trio`.toLowerCase()
    return words.every((w) => about.includes(w))
  })
  if (query.sort === "price-asc") return kept.sort((a, b) => Number(a.price) - Number(b.price))
  if (query.sort === "price-desc") return kept.sort((a, b) => Number(b.price) - Number(a.price))
  if (query.sort === "name") return kept.sort((a, b) => a.name.localeCompare(b.name))
  return kept
}
